import React, { useState, useEffect } from 'react';

const API_BASE = import.meta?.env?.VITE_API_URL || 'http://45.32.118.117:8090';

const WeeklyPerformance = ({ accountId }) => {
  const [weeklyData, setWeeklyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(12); // Default: Last 12 weeks

  useEffect(() => {
    if (!accountId) return;
    fetchWeeklyPerformance();
  }, [accountId]);

  const fetchWeeklyPerformance = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      
      const response = await fetch(
        `${API_BASE}/api/trader/trades?account_id=${accountId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const weeklyPerf = calculateWeeklyPerformance(data.trades || []);
      setWeeklyData(weeklyPerf);
    } catch (err) {
      console.error('Failed to fetch weekly performance:', err);
      setWeeklyData([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateWeeklyPerformance = (trades) => {
    const weeks = {};
    
    trades.forEach(trade => {
      if (!trade.close_time) return;
      
      const closeDate = new Date(trade.close_time);
      const weekStart = new Date(closeDate);
      weekStart.setDate(closeDate.getDate() - closeDate.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = {
          weekStart: weekKey,
          profit: 0,
          trades: 0,
          wins: 0,
          losses: 0
        };
      }
      
      const profitValue = parseFloat(trade.profit || 0) + 
                          parseFloat(trade.swap || 0) + 
                          parseFloat(trade.commission || 0);
      
      weeks[weekKey].profit += profitValue;
      weeks[weekKey].trades += 1;
      if (profitValue > 0) weeks[weekKey].wins += 1;
      if (profitValue < 0) weeks[weekKey].losses += 1;
    });
    
    return Object.values(weeks)
      .sort((a, b) => new Date(a.weekStart) - new Date(b.weekStart));
  };

  if (loading) {
    return (
      <div style={{
        padding: 16,
        background: '#1a1a1a',
        borderRadius: 12,
        marginBottom: 24,
        color: '#6b7280'
      }}>
        Loading weekly performance...
      </div>
    );
  }

  if (!weeklyData || weeklyData.length === 0) {
    return (
      <div style={{
        padding: 16,
        background: '#1a1a1a',
        borderRadius: 12,
        marginBottom: 24,
        color: '#6b7280'
      }}>
        No weekly data available
      </div>
    );
  }

  // Get last N weeks based on selected period
  const displayData = weeklyData.slice(-selectedPeriod);
  
  if (displayData.length === 0) {
    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12
        }}>
          <h3 style={{
            fontSize: 16,
            fontWeight: 600,
            color: '#fff',
            margin: 0
          }}>
            Weekly Performance
          </h3>
          
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(parseInt(e.target.value))}
            style={{
              padding: '6px 12px',
              background: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: 6,
              color: '#fff',
              fontSize: 14,
              cursor: 'pointer'
            }}
          >
            <option value={4}>Last 4 weeks</option>
            <option value={8}>Last 8 weeks</option>
            <option value={12}>Last 12 weeks</option>
            <option value={26}>Last 26 weeks</option>
          </select>
        </div>
        
        <div style={{
          padding: 16,
          background: '#1a1a1a',
          borderRadius: 12,
          color: '#6b7280'
        }}>
          No data for selected period
        </div>
      </div>
    );
  }

  const maxProfit = Math.max(...displayData.map(w => Math.abs(w.profit)));

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12
      }}>
        <h3 style={{
          fontSize: 16,
          fontWeight: 600,
          color: '#fff',
          margin: 0
        }}>
          Weekly Performance
        </h3>
        
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(parseInt(e.target.value))}
          style={{
            padding: '6px 12px',
            background: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: 6,
            color: '#fff',
            fontSize: 14,
            cursor: 'pointer'
          }}
        >
          <option value={4}>Last 4 weeks</option>
          <option value={8}>Last 8 weeks</option>
          <option value={12}>Last 12 weeks</option>
          <option value={26}>Last 26 weeks</option>
        </select>
      </div>
      
      <div style={{
        padding: 16,
        background: '#1a1a1a',
        borderRadius: 12
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 6,
          height: 150
        }}>
          {displayData.map((week, idx) => {
            const barHeight = maxProfit > 0 
              ? (Math.abs(week.profit) / maxProfit) * 130 
              : 0;
            const isProfit = week.profit >= 0;
            const weekDate = new Date(week.weekStart);
            const weekLabel = `${weekDate.getMonth() + 1}/${weekDate.getDate()}`;

            return (
              <div
                key={idx}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: barHeight || 4,
                    background: isProfit ? '#10b981' : '#ef4444',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  title={`Week ${weekLabel}: $${week.profit.toFixed(2)} (${week.trades} trades)`}
                />
                <div style={{
                  fontSize: 10,
                  color: '#9ca3af',
                  fontWeight: 500,
                  textAlign: 'center'
                }}>
                  {weekLabel}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{
          marginTop: 16,
          paddingTop: 12,
          borderTop: '1px solid #333',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
          gap: 12,
          fontSize: 12,
          color: '#9ca3af'
        }}>
          <div>
            <span style={{ color: '#6b7280' }}>Weeks:</span>{' '}
            <span style={{ color: '#fff', fontWeight: 600 }}>{displayData.length}</span>
          </div>
          <div>
            <span style={{ color: '#6b7280' }}>Profitable:</span>{' '}
            <span style={{ color: '#10b981', fontWeight: 600 }}>
              {displayData.filter(w => w.profit > 0).length}
            </span>
          </div>
          <div>
            <span style={{ color: '#6b7280' }}>Loss:</span>{' '}
            <span style={{ color: '#ef4444', fontWeight: 600 }}>
              {displayData.filter(w => w.profit < 0).length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyPerformance;
